import { useNavigate, useOutletContext } from 'react-router-dom';

import { useFormik } from 'formik';

import { upsertBillingProvider } from 'api/Admin/Practice/Providers';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';
import { withErrorBoundary } from 'components/shared/Error/Boundary';

import getInitialValues from '../lib/getInitialValues';
import PracticeBillingValidationSchema from '../lib/practiceBillingValidationSchema';

import CreateUpdateBillingProviderForm from './CreateUpdateBillingProviderForm';

const CreateUpdateBillingProvider = ({ isOpen, handleClose, item }) => {
  const { data: statesData } = useStates();
  const states = optionify(statesData?.states);
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(item, practice),
    validationSchema: PracticeBillingValidationSchema,
    onSubmit: async (values) => {
      const hasNpiAndTaxId = values.bill_npi && values.bill_taxid;
      const hasTaxonomyAndId = values.bill_taxonomy && values.bill_id;

      if (!hasNpiAndTaxId && !hasTaxonomyAndId) {
        showAlert({
          title: 'Please provide either NPI & Tax ID or Taxonomy & ID.',
          color: 'danger'
        });
        return;
      }

      mutateBillingProvider.mutate(values);
    }
  });

  const loadedMembers = iaRa(practice?.members).map((member) => {
    return { label: `${member?.f_name} ${member?.l_name}`, value: member?.id };
  });

  const mutateBillingProvider = useCustomMutation({
    mutationFn: (values) =>
      upsertBillingProvider(navigate, {
        newBillingProvider: {
          ...values,
          bill_taxid_type: values.bill_taxid_type?.value
        }
      }),
    invalidateQueryKey: ['billing-provider'],
    successMessage: item
      ? 'Billing Provider updated successfully!'
      : 'Billing Provider created successfully!',
    errorMessage: item
      ? 'Failed to update Billing Provider!'
      : 'Failed to create Billing Provider!',
    onSuccess: () => {
      formik.resetForm();
      handleClose();
    }
  });

  if (loadedMembers.length === 0) {
    return null;
  }

  return (
    <CreateUpdateBillingProviderForm
      formik={formik}
      open={isOpen}
      type={item !== null ? 'update' : 'create'}
      handleClose={() => handleClose()}
      isLoading={mutateBillingProvider.isLoading}
      states={states}
      loadedMembers={loadedMembers}
      editForm={!!item}
    />
  );
};

export default withErrorBoundary(CreateUpdateBillingProvider);
